import median from 'median-js-bridge';

export const IOS_APP_STORE_URL =
    'https://apps.apple.com/au/app/7day-app/id6470282265?itsct=apps_box_link&itscg=30200';

export const ANDROID_PACKAGE = 'com.sevenday.mobile';

export const ANDROID_PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}&pcampaignid=web_share7DAY`;

const DISMISS_KEY = '7day.appPromptDismissed';

export type MobilePlatform = 'ios' | 'android';

const NON_SAFARI_IOS = /CriOS|FxiOS|EdgiOS|OPiOS/;

export function getMobilePlatform(
    userAgent: string,
    maxTouchPoints: number,
): MobilePlatform | null {
    if (/iPad|iPhone|iPod/.test(userAgent)) {
        return 'ios';
    }
    // iPadOS 13+ reports a desktop Macintosh user agent.
    if (/Mac/.test(userAgent) && maxTouchPoints > 1) {
        return 'ios';
    }
    if (/Android/i.test(userAgent)) {
        return 'android';
    }
    return null;
}

export function isIosSafari(
    userAgent: string,
    maxTouchPoints: number,
): boolean {
    if (getMobilePlatform(userAgent, maxTouchPoints) !== 'ios') {
        return false;
    }
    if (NON_SAFARI_IOS.test(userAgent)) {
        return false;
    }
    return /Version\//.test(userAgent) && /Safari/.test(userAgent);
}

export function storeUrlForPlatform(platform: MobilePlatform): string {
    return platform === 'ios' ? IOS_APP_STORE_URL : ANDROID_PLAY_STORE_URL;
}

export function androidIntentUrl(
    pageUrl: string,
    fallbackUrl = ANDROID_PLAY_STORE_URL,
): string {
    const url = new URL(pageUrl);
    const target = `${url.host}${url.pathname}${url.search}`;
    const scheme = url.protocol.replace(':', '');
    const fallback = encodeURIComponent(fallbackUrl);
    return `intent://${target}#Intent;scheme=${scheme};package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
}

export function openAppDownload(
    platform: MobilePlatform,
    pageUrl = window.location.href,
) {
    const destination =
        platform === 'android'
            ? androidIntentUrl(pageUrl)
            : storeUrlForPlatform(platform);
    window.location.assign(destination);
}

function safeGetLocalStorageItem(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeSetLocalStorageItem(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // ignore storage failures (private mode, quota, etc.)
    }
}

export function isAppPromptDismissed(): boolean {
    return safeGetLocalStorageItem(DISMISS_KEY) === 'true';
}

export function dismissAppPrompt() {
    safeSetLocalStorageItem(DISMISS_KEY, 'true');
}

export function shouldShowAppPrompt(
    userAgent: string,
    maxTouchPoints: number,
    dismissed = isAppPromptDismissed(),
    inNativeApp = median.isNativeApp(),
): boolean {
    if (dismissed || inNativeApp) return false;
    if (isIosSafari(userAgent, maxTouchPoints)) return false;
    return getMobilePlatform(userAgent, maxTouchPoints) !== null;
}
